import React from "react";
import { Database } from "lucide-react";
import GlobalDetailDrawer from "modules/swiftboard/components/GlobalDetailDrawer";
import LocalDateTime from "services/date-converter";
import MessageList from "../../../components/MessageList";
import LifecycleBadge from "../../../components/LifecycleBadge";
import { DEFAULT_PAGE_SIZE } from "../features/source.constants";
import { DATA_TYPE_LABEL, FIELD_ROLE_LABEL, SUBJECT_TYPE_LABEL } from "../../../features/builder.constants";
import { toJsonText } from "../../../features/builder.utils";

function FieldList({ fields }) {
  if (!fields.length) return <p className="text-xs text-text_muted">No fields yet. Tick columns with Edit.</p>;
  return (
    <ul className="divide-y divide-border">
      {fields.map((field) => (
        <li key={field.id} className="flex items-center justify-between gap-3 py-1.5">
          <span className="min-w-0 truncate text-xs text-text_primary">
            {field.label || field.field_key}{" "}
            <span className="font-mono text-[11px] text-text_muted">{field.path || `computed: ${field.compute?.op ?? ""}`}</span>
          </span>
          <span className="shrink-0 text-[11px] text-text_muted">
            {DATA_TYPE_LABEL[field.data_type] ?? field.data_type} · {FIELD_ROLE_LABEL[field.field_role] ?? field.field_role}
          </span>
        </li>
      ))}
    </ul>
  );
}

// Read-only view of one source (fetched with its fields by the list page).
export default function SourceViewDrawer({ record, onClose }) {
  const fields = record?.fields ?? [];
  const validation = record?.validation_message?.split("\n").filter(Boolean) ?? [];

  const sections = [
    {
      title: "Source",
      fields: [
        { label: "Label", value: record.label || "—" },
        { label: "Key", value: <span className="font-mono">{record.source_key}</span> },
        { label: "Root model", value: <span className="font-mono">{record.root_model}</span> },
        { label: "Module", value: record.module_label || "—" },
        { label: "Subject", value: SUBJECT_TYPE_LABEL[record.subject_type] || "—" },
        { label: "Lifecycle", value: <LifecycleBadge status={record.status} version={record.version_no} /> },
        { label: "Published", value: record.published_at ? <LocalDateTime utcTime={record.published_at} /> : "—" },
        { label: "Validated", value: record.validated_at ? <LocalDateTime utcTime={record.validated_at} /> : "—" },
        { label: "Active", value: record.is_activated ? "Yes" : "No" },
        { label: "Description", value: record.description || "—" },
      ],
    },
    ...(validation.length
      ? [{ title: record.status === "ACTIVE" ? "Warnings" : "Publish problems", fields: [{ value: <MessageList tone={record.status === "ACTIVE" ? "warning" : "problem"} items={validation} /> }] }]
      : []),
    {
      title: "What is read",
      fields: [
        { label: "Relations", value: (record.relations ?? []).length ? <span className="font-mono">{record.relations.join(", ")}</span> : "None" },
        { label: "Page size", value: (record.page_size ?? DEFAULT_PAGE_SIZE).toLocaleString() },
        {
          label: "Base filter",
          value: record.base_where ? <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px]">{toJsonText(record.base_where)}</pre> : "None",
        },
      ],
    },
    { title: `Fields (${fields.length})`, fields: [{ value: <FieldList fields={fields} /> }] },
  ];

  return <GlobalDetailDrawer open onClose={onClose} title={record.label || "Source"} subtitle={record.source_key} icon={<Database size={20} />} sections={sections} />;
}
